class CompareUtils {
  static cookieName = 'hypertheme:compare-products';

  /**
   *
   * @returns {array}
   */
  static getSelectedProducts() {
    const products = FoxTheme.utils.getStorage(this.cookieName);
    return products || [];
  }

  static setSelectedProducts(products) {
    if (products.length > 0) {
      FoxTheme.utils.setStorage(this.cookieName, products);
    } else {
      localStorage.removeItem(this.cookieName);
    }
  }

  static getProductsCount() {
    return this.getSelectedProducts().length;
  }

  static getMaxProducts() {
    return window.matchMedia(FoxTheme.config.mediaQueryMobile).matches
      ? FoxTheme.compare.maxProductsInCompareMobile
      : FoxTheme.compare.maxProductsInCompareDesktop;
  }

  static getAlertMessage() {
    return window.matchMedia(FoxTheme.config.mediaQueryMobile).matches
      ? FoxTheme.compare.alertMessageMobile
      : FoxTheme.compare.alertMessageDesktop;
  }

  /**
   * Check the given product id in compare list.
   * @param {*} productId
   * @returns {boolean}
   */
  static isProductInList(productId) {
    const products = this.getSelectedProducts();

    return products.find((product) => product.id === productId);
  }

  /**
   *
   * @param {*} productId
   * @param {*} productUrl
   * @returns {boolean} True if the product added to list.
   */
  static addToList(productId, productUrl) {
    let products = FoxTheme.utils.getStorage(this.cookieName) || [];

    if (products.length < this.getMaxProducts()) {
      if (!products.some((product) => product.id === productId)) {
        products.push({ id: productId, url: productUrl });
      }

      this.setSelectedProducts(products);
      this.updateCompareCheckboxes(products);

      FoxTheme.pubsub.publish('compare:add', { products: products, productId: productId, productUrl: productUrl });

      return true;
    } else {
      alert(this.getAlertMessage());

      return false;
    }
  }

  static removeFromList(productId) {
    let products = this.getSelectedProducts();
    if (products) {
      products = products.filter((product) => product.id !== productId);
    }
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes(products);
    FoxTheme.pubsub.publish('compare:remove', { products: products, productId: productId });
  }

  static clearList() {
    const products = this.getSelectedProducts();
    if (products) {
      products.forEach((product) => {
        this.removeFromList(product.id);
      });
    }
  }

  static updateCompareCheckboxes(products = null) {
    products = products || this.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = products.filter((product) => product.id === checkbox.dataset.productId).length;
    });
  }

  static getTemplateUrl(productUrl, queryObj = {}) {
    let url = productUrl;
    const separator = productUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();

    for (const key in queryObj) {
      if (Object.prototype.hasOwnProperty.call(queryObj, key)) {
        params.append(key, queryObj[key]);
      }
    }

    // Append parameters to the URL if any were added.
    if (Array.from(params).length > 0) {
      url += `${separator}${params.toString()}`;
    }

    return url;
  }

  static getCompareBarItemTemplate(productUrl) {
    return this.getTemplateUrl(productUrl, {
      sections: 'product-compare-bar-item',
    });
  }
}

class CompareSwitch extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'hypertheme:compare-is-active';

    this.classes = {
      isActive: 'is-product-comparing',
    };

    this.checkboxEl = this.querySelector('input');
    this.buttonEl = this.querySelector('.collection-compare-launcher__button') || this.querySelector('label');
    this.compareIsActive = false;
    this.onChangeHandler = this.onChange.bind(this);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    this.onFloatingVisibilityHandler = this.onFloatingVisibility.bind(this);
    this.onCompareToggleHandler = this.onCompareToggle.bind(this);
  }

  connectedCallback() {
    this.init();
    if (this.buttonEl) {
      this.buttonEl.addEventListener('click', this.onButtonClickHandler);
    }
    if (this.checkboxEl) {
      this.checkboxEl.addEventListener('change', this.onChangeHandler);
    }
    this.compareToggleUnsubscriber = FoxTheme.pubsub.subscribe('compare:toggle', this.onCompareToggleHandler);

    if (this.classList.contains('collection-compare-launcher')) {
      window.addEventListener('floating-controls:visibility', this.onFloatingVisibilityHandler);
      this.classList.toggle('is-visible', window.nextopChatVisible === true);
      this.dataset.floatingReady = 'true';
      window.dispatchEvent(new CustomEvent('floating-control:ready'));
    }
  }

  disconnectedCallback() {
    if (this.buttonEl) {
      this.buttonEl.removeEventListener('click', this.onButtonClickHandler);
    }
    if (this.checkboxEl) {
      this.checkboxEl.removeEventListener('change', this.onChangeHandler);
    }
    window.removeEventListener('floating-controls:visibility', this.onFloatingVisibilityHandler);
    if (this.compareToggleUnsubscriber) {
      this.compareToggleUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const nextState = !document.body.classList.contains(this.classes.isActive);
    this.handleChange(nextState);
  }

  onCompareToggle(data) {
    if (data?.source === this) return;
    if (typeof data?.isActive === 'boolean') {
      this.applyState(data.isActive, false);
    }
  }

  init() {
    const products = CompareUtils.getSelectedProducts();
    let isActive = document.body.classList.contains(this.classes.isActive);
    if (!isActive && products && products.length > 0) {
      isActive = true;
    }
    this.applyState(isActive, false);
  }

  onChange() {
    if (this.checkboxEl) {
      this.handleChange(this.checkboxEl.checked);
    }
  }

  onFloatingVisibility(event) {
    this.classList.toggle('is-visible', event.detail.chatVisible === true);
  }

  applyState(isActive, notify = true) {
    const nextActive = Boolean(isActive);
    this.compareIsActive = nextActive;
    if (this.checkboxEl) {
      this.checkboxEl.checked = nextActive;
    }
    this.classList.toggle('is-active', nextActive);
    document.body.classList.toggle(this.classes.isActive, nextActive);

    if (nextActive) {
      const compareBar = document.querySelector('compare-bar');
      if (compareBar && typeof compareBar.setHeight === 'function') {
        compareBar.setHeight();
      }
    } else {
      document.documentElement.style.setProperty('--compare-bar-height', '0px');
    }

    if (notify) {
      window.dispatchEvent(new CustomEvent('compare-bar:visibility', { detail: { isActive: nextActive } }));
    }
  }

  handleChange(isActive) {
    const nextActive = Boolean(isActive);
    this.applyState(nextActive, true);

    FoxTheme.pubsub.publish('compare:toggle', { isActive: nextActive, source: this });

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, nextActive);
    }
  }
}
customElements.define('compare-switch', CompareSwitch);

class CompareCheckbox extends HTMLElement {
  constructor() {
    super();

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    const { productId } = this.checkboxEl.dataset;
    this.checkboxEl.checked = CompareUtils.isProductInList(productId);
  }

  onChange(evt) {
    evt.preventDefault();

    const { productId, productUrl } = this.checkboxEl.dataset;

    if (this.checkboxEl.checked) {
      const isAdded = CompareUtils.addToList(productId, productUrl);
      if (!isAdded) {
        this.checkboxEl.checked = false;
        return;
      }

      const compareSwitchEl = document.querySelector('compare-switch input');
      if (compareSwitchEl && !compareSwitchEl.checked) {
        compareSwitchEl.checked = true;
        compareSwitchEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      CompareUtils.removeFromList(productId);
    }
  }
}
customElements.define('compare-checkbox', CompareCheckbox);

class CompareRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.onClickHandler = this.onClick.bind(this);
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    const { productId } = this.dataset;
    const { target } = event;

    CompareUtils.removeFromList(productId);

    const compareModal = target.closest('.compare-drawer');
    if (compareModal) {
      compareModal.updateProductsCount();
    }

    if (CompareUtils.getProductsCount() < 2) {
      compareModal && compareModal.hide();
    } else {
      const compareTable = target.closest('.compare-table');
      if (compareTable) {
        const cell = target.closest('td');
        const colIndex = cell.cellIndex;

        const rows = compareTable.querySelectorAll('tr');
        rows.forEach((row) => {
          const cellToRemove = row.cells[colIndex];
          if (cellToRemove) {
            cellToRemove.remove();
          }
        });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }
}
customElements.define('compare-remove', CompareRemove, { extends: 'button' });

class CompareBar extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      openBtn: '.compare-bar__open',
      clearBtns: ['.compare-bar__clear'],
      closeBtn: '.compare-bar__close',
      toggleList: '.compare-bar__toggle-list',
      list: '.compare-bar__list',
      counter: '.compare-bar__counter',
    };

    this.classes = {
      listVisible: 'is-show-list',
    };

    this.abortController = new AbortController();
    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    this.atLeastItemToShow = Math.min(5, CompareUtils.getMaxProducts()); // Total compare products and placeholders.
    this.mediaQueryList = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    this.onMediaChangeHandler = this.onMediaChange.bind(this);
    this.isEmptyItemScrolling = false;

    this.elements.clearBtns.forEach((clearBtn) => {
      clearBtn.addEventListener('click', this.handleClear.bind(this), {
        signal: this.abortController.signal,
      });
    });

    this.elements.toggleList.addEventListener('click', this.handleToggleList.bind(this), {
      signal: this.abortController.signal,
    });

    this.elements.closeBtn.addEventListener('click', this.handleClose.bind(this), {
      signal: this.abortController.signal,
    });

    this.elements.list.addEventListener('click', this.handleEmptyItemClick.bind(this), {
      signal: this.abortController.signal,
    });

    this.itemAddUnsubscriber = FoxTheme.pubsub.subscribe('compare:add', this.onItemAdded.bind(this));
    this.itemRemoveUnsubscriber = FoxTheme.pubsub.subscribe('compare:remove', this.onItemRemoved.bind(this));
  }

  itemAddUnsubscriber = undefined;
  itemRemoveUnsubscriber = undefined;

  connectedCallback() {
    this.renderBar();
    this.mediaQueryList.addEventListener('change', this.onMediaChangeHandler);
    this.setHeight();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.mediaQueryList.removeEventListener('change', this.onMediaChangeHandler);

    if (this.itemAddUnsubscriber) {
      this.itemAddUnsubscriber();
    }

    if (this.itemRemoveUnsubscriber) {
      this.itemRemoveUnsubscriber();
    }
  }

  onItemAdded(event) {
    const { products, productId, productUrl } = event;

    this.appendBarItem(products, productId, productUrl);
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
  }

  onItemRemoved(event) {
    const { products, productId } = event;
    const itemToDelete = this.elements.list.querySelector(`.compare-bar__item[data-product-id="${productId}"]`);

    itemToDelete && itemToDelete.remove();
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
    this.removePlaceholdes();
    this.addPlaceholders(products.length);
  }

  onMediaChange() {
    const productsCount = CompareUtils.getProductsCount();
    this.atLeastItemToShow = Math.min(5, CompareUtils.getMaxProducts());
    this.removePlaceholdes();
    this.addPlaceholders(productsCount);
    this.setHeight();
  }

  async renderBar() {
    const products = CompareUtils.getSelectedProducts();

    let output = '';
    const compareItemHtmls = await this.getBarItems(products);
    compareItemHtmls.forEach((htmlStr) => {
      output += htmlStr;
    });

    this.removePlaceholdes();
    this.elements.list.innerHTML = output;
    this.addPlaceholders(products.length);

    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
    this.syncVisibility(products.length);
  }

  async getBarItems(products) {
    const tmps = [];
    await Promise.all(
      products.map(async (product) => {
        const fetchUrl = CompareUtils.getCompareBarItemTemplate(product.url);
        await fetch(CompareUtils.getCompareBarItemTemplate(product.url))
          .then((response) => response.json())
          .then((response) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
            const itemHtml = doc.querySelector('.shopify-section');

            tmps[product.id] = itemHtml.innerHTML;
          });
      })
    );

    const returns = [];
    products.forEach((product) => returns.push(tmps[product.id]));
    return returns;
  }

  async appendBarItem(products, productId, productUrl) {
    this.removePlaceholdes();
    this.elements.list.insertAdjacentHTML(
      'beforeend',
      `<li class="compare-bar__item" data-product-id="${productId}"></li>`
    );
    this.addPlaceholders(products.length);

    await fetch(CompareUtils.getCompareBarItemTemplate(productUrl))
      .then((response) => response.json())
      .then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
        const itemHtml = doc.querySelector('.shopify-section');

        const itemEl = this.elements.list.querySelector('[data-product-id="' + productId + '"]');
        if (itemEl) {
          itemEl.outerHTML = itemHtml.innerHTML;
        }
      });
  }

  async handleEmptyItemClick(event) {
    const targetItem = event.target.closest('.compare-bar__item');
    if (!targetItem || !this.elements.list.contains(targetItem) || this.isEmptyItemScrolling) return;

    if (!this.hasRequiredEmptyItems()) return;

    const firstCompareCheckbox = document.querySelector('#ProductsList .product-card compare-checkbox');
    const firstProductCard = firstCompareCheckbox?.closest('.product-card');

    if (!firstProductCard) return;

    event.preventDefault();
    this.isEmptyItemScrolling = true;

    await this.scrollCardIntoView(firstProductCard);
    this.isEmptyItemScrolling = false;
  }

  hasRequiredEmptyItems() {
    const items = Array.from(this.elements.list.querySelectorAll('.compare-bar__item'));
    const requiredItemCount = this.mediaQueryList.matches ? this.atLeastItemToShow : 3;

    return (
      items.length === requiredItemCount &&
      items.every(
        (item) => item.classList.contains('compare-bar__item-placeholder') && item.childElementCount === 0
      )
    );
  }

  scrollCardIntoView(productCard) {
    const cardRect = productCard.getBoundingClientRect();
    const compareBarHeight = this.getBoundingClientRect().height;
    const availableViewportHeight = Math.max(window.innerHeight - compareBarHeight, 1);
    const targetScrollTop = Math.max(
      0,
      window.scrollY + cardRect.top - Math.max((availableViewportHeight - cardRect.height) / 2, 0)
    );

    window.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    });

    return new Promise((resolve) => {
      const startedAt = performance.now();
      let lastScrollAt = startedAt;

      const handleScroll = () => {
        lastScrollAt = performance.now();
      };

      const checkScrollPosition = (now) => {
        const hasSettled = now - lastScrollAt > 120 && now - startedAt > 160;
        const hasTimedOut = now - startedAt > 1400;

        if (hasSettled || hasTimedOut) {
          window.removeEventListener('scroll', handleScroll);
          resolve();
          return;
        }

        requestAnimationFrame(checkScrollPosition);
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      requestAnimationFrame(checkScrollPosition);
    });
  }

  handleToggleList(event) {
    event.preventDefault();

    if (this.classList.contains(this.classes.listVisible)) {
      this.classList.remove(this.classes.listVisible);
    } else {
      this.classList.add(this.classes.listVisible);
    }

    this.setHeight();
  }

  handleClear() {
    CompareUtils.clearList();
  }

  handleClose() {
    const compareSwitch = document.querySelector('compare-switch');
    if (compareSwitch && typeof compareSwitch.handleChange === 'function') {
      compareSwitch.handleChange(false);
    } else {
      const compareSwitchEl = document.querySelector('compare-switch input');
      if (compareSwitchEl) {
        compareSwitchEl.checked = false;
      }
      document.body.classList.remove('is-product-comparing');
      document.documentElement.style.setProperty('--compare-bar-height', '0px');
      FoxTheme.pubsub.publish('compare:toggle', { isActive: false });
      window.dispatchEvent(new CustomEvent('compare-bar:visibility', { detail: { isActive: false } }));
    }
  }

  toggleCompareButton(count) {
    this.elements.openBtn.disabled = !(count > 1);
  }

  updateCounters(count) {
    this.elements.counter.innerText = count;
  }

  syncVisibility(count) {
    if (count === 0) return;

    const compareSwitch = document.querySelector('compare-switch');
    if (compareSwitch && typeof compareSwitch.handleChange === 'function') {
      compareSwitch.handleChange(true);
    } else {
      document.body.classList.add('is-product-comparing');
      const compareSwitchInput = document.querySelector('compare-switch input');
      if (compareSwitchInput) {
        compareSwitchInput.checked = true;
      }
      if (compareSwitch) {
        compareSwitch.classList.add('is-active');
        compareSwitch.compareIsActive = true;
      }
      this.setHeight();
      window.dispatchEvent(new CustomEvent('compare-bar:visibility', { detail: { isActive: true } }));
    }
  }

  removePlaceholdes() {
    Array.from(this.elements.list.querySelectorAll('li')).forEach((li) => {
      if (li.classList.contains('compare-bar__item-placeholder')) {
        li.remove();
      }
    });
  }

  addPlaceholders(count) {
    const placeholderCount = this.atLeastItemToShow - count;

    for (let i = 0; i < placeholderCount; i++) {
      const placeholderItem = document.createElement('li');

      placeholderItem.classList.add('compare-bar__item', 'compare-bar__item-placeholder');
      this.elements.list.appendChild(placeholderItem);
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.round(window.innerHeight - rect.top));
      const offsetHeight = this.classList.contains('is-show-list') ? Math.round(this.offsetHeight) : visibleHeight;
      document.documentElement.style.setProperty('--compare-bar-height', `${offsetHeight}px`);
      window.dispatchEvent(new CustomEvent('compare-bar:visibility'));
    });
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      table: '.compare-table',
    };

    this.classesToRemoveOnLoad = 'drawer--loading';
  }

  get requiresBodyAppended() {
    return false;
  }

  get selector() {
    return '.product-comparison-table';
  }

  get sourceSelector() {
    return '#MainProduct-compare__rows';
  }

  get placeholderSelector() {
    return '#MainProduct-compare__placeholder';
  }

  getSectionId() {
    let sectionId = FoxTheme.ProductCompareSectionId || false;

    if (!sectionId) {
      // Get section id from overlay groups.
      const sectionEl = document.querySelector('.section-group-overlay-product-compare');
      if (sectionEl) {
        sectionId = FoxTheme.utils.getSectionId(sectionEl);
      }

      // Cache for better performance.
      FoxTheme.ProductCompareSectionId = sectionId;
    }

    return sectionId;
  }

  // shouleBeShow() {
  //   const sectionId = this.getSectionId();
  //   return typeof sectionId === 'string';
  // }

  prepareToShow() {
    super.prepareToShow();
    this.renderCompareTable();
  }

  async renderCompareTable() {
    const drawerContent = this.querySelector(this.selector);
    const sectionId = this.getSectionId();

    const products = CompareUtils.getSelectedProducts();

    this.updateProductsCount();

    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    const tableRows = this.elements.table.querySelectorAll('tbody tr');
    let compareRows = await this.getCompareTableRows(products);

    if (Shopify.designMode) {
      // Append placeholder items in editor mode.
      let maxPlaceholderItems = 3;

      if (compareRows.length == 2) {
        maxPlaceholderItems = 2;
      }

      let numberPlaceholderItems =
        compareRows.length > 0 ? maxPlaceholderItems - compareRows.length : maxPlaceholderItems;

      if (numberPlaceholderItems > 0) {
        const placeholderRows = this.getCompareTablePlaceholderRows(numberPlaceholderItems);

        compareRows = compareRows.concat(placeholderRows);

        this.updateProductsCount(maxPlaceholderItems);
      }
    }

    compareRows.forEach((compareRow) => {
      tableRows.forEach((row) => {
        const compareKey = row.dataset.compareKey;
        const compareCol = compareRow.querySelector(`td[data-compare-key="${compareKey}"]`);
        row.appendChild(compareCol);
      });
    });

    setTimeout(() => {
      this.classList.remove(this.classesToRemoveOnLoad);
    }, 300);
  }

  updateProductsCount(count) {
    this.style.setProperty('--number-items', count || CompareUtils.getProductsCount());
  }

  async getCompareTableRows(products) {
    const tmps = [];
    const sectionId = this.getSectionId();
    await Promise.all(
      products.map(async (product) => {
        const fetchUrl = CompareUtils.getTemplateUrl(product.url, {
          section_id: sectionId,
        });
        await fetch(fetchUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const rowTemplate = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.sourceSelector);

            tmps[product.id] = rowTemplate.content.cloneNode(true);
          });
      })
    );

    const returns = [];
    products.forEach((product) => returns.push(tmps[product.id]));
    return returns;
  }

  getCompareTablePlaceholderRows(count) {
    const rowTemplate = this.querySelector(this.placeholderSelector);

    const returns = [];
    for (let i = 0; i < count; i++) {
      returns.push(rowTemplate.content.cloneNode(true));
    }

    return returns;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleAfterHide();
  }

  handleAfterHide() {
    super.handleAfterHide();
    // Clean the table.
    this.querySelectorAll('.compare-table > tbody > tr').forEach((row) => {
      const nonLabelCells = row.querySelectorAll('td:not(.td-label)');
      nonLabelCells.forEach((cell) => {
        cell.remove();
      });
    });

    this.classList.add(this.classesToRemoveOnLoad);
  }
}
customElements.define('compare-drawer', CompareDrawer);
